import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  centralMinute,
  coverageSegments,
  createBlobCache,
  fetchRelayBlobUrl,
  fetchRelayJson,
  fmtClock,
  fmtDateLong,
  listUrl,
  nearestClip,
  sortClips,
  thumbUrl,
  todayCentral,
} from '../../utils/cameraRelay';
import { runAt } from '../../utils/stationActivity';
import DayBar, { RANGE_FULL, RANGE_WORKDAY, coverageToBlocks, runsToBlocks } from './DayBar';
import { C, KIND_DOT } from './CamerasStyles';
import { useStationRuns } from './useCameraData';

const STEPS = [[-10, '−10 min'], [-2, '−2 min'], [2, '+2 min'], [10, '+10 min']];

/** Every camera at one moment, with the shop's JobQ work underneath. */
export default function AllCamerasView({ cameras, aspects, date, minute, onMinute, onOpenCamera }) {
  const thumbCache = useRef(createBlobCache(120));
  const [clipsByCam, setClipsByCam] = useState({});
  const [loading, setLoading] = useState(true);
  const [thumbs, setThumbs] = useState({});
  const [range, setRange] = useState('workday');

  const stations = useMemo(() => [...new Set(cameras.map((c) => c.jobqStation).filter(Boolean))], [cameras]);
  const runsState = useStationRuns(stations, date);
  const camKey = cameras.map((c) => c.key).join(',');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all(cameras.map((c) =>
      fetchRelayJson(listUrl(c.station, c.index, date))
        .then((d) => [c.key, sortClips(d?.files)])
        .catch(() => [c.key, []]),
    )).then((pairs) => {
      if (cancelled) return;
      setClipsByCam(Object.fromEntries(pairs));
      setLoading(false);
    });
    return () => { cancelled = true; };
    // camKey stands for the camera list
  }, [camKey, date]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const cache = thumbCache.current;
    return () => cache.clear();
  }, []);

  // The clip nearest the chosen moment, per camera.
  const picks = useMemo(() => {
    const out = {};
    for (const c of cameras) out[c.key] = nearestClip(clipsByCam[c.key] || [], minute);
    return out;
  }, [cameras, clipsByCam, minute]);

  useEffect(() => {
    let cancelled = false;
    cameras.forEach((c) => {
      const clip = picks[c.key];
      if (!clip) return;
      const key = `${c.key}|${clip.file}`;
      const hit = thumbCache.current.get(key);
      if (hit) {
        setThumbs((prev) => (prev[c.key] === hit ? prev : { ...prev, [c.key]: hit }));
        return;
      }
      fetchRelayBlobUrl(thumbUrl(c.station, c.index, clip.file))
        .then((url) => {
          thumbCache.current.set(key, url);
          if (!cancelled) setThumbs((prev) => ({ ...prev, [c.key]: url }));
        })
        .catch(() => {});
    });
    return () => { cancelled = true; };
  }, [cameras, picks]);

  const union = useMemo(() => {
    const all = Object.values(clipsByCam).flat().sort((a, b) => a.start - b.start);
    return coverageSegments(all);
  }, [clipsByCam]);

  const activeRange = range === 'full' || minute < RANGE_WORKDAY[0] || minute > RANGE_WORKDAY[1] ? RANGE_FULL : RANGE_WORKDAY;
  const lanes = [
    { key: 'rec', label: 'Recorded', height: 10, blocks: coverageToBlocks(union) },
    ...stations.map((s) => ({ key: s, label: s, height: 22, minLabelPct: 2.8, blocks: runsToBlocks(runsState[s]?.runs) })),
  ];
  const isToday = date === todayCentral();

  return (
    <main style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '14px 24px 28px' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 16, fontWeight: 500, marginRight: 6 }}>{fmtClock(minute)}</span>
        <span style={{ color: C.faint, fontSize: 12, marginRight: 6 }}>every camera at the same moment · click one to play it</span>
        {STEPS.map(([d, label]) => (
          <button key={d} type="button" className="jqc-btn" onClick={() => onMinute(Math.max(0, Math.min(1439, minute + d)))}>{label}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: 48, textAlign: 'center', color: C.muted }}>Loading recordings for every camera…</div>
      ) : (
        <div className="jqc-grid" style={{ '--jqc-min': '320px' }}>
          {cameras.map((c) => {
            const clip = picks[c.key];
            const wide = (aspects[c.key] || c.aspect) > 2.4;
            const run = c.jobqStation ? runAt(runsState[c.jobqStation]?.runs || [], minute) : null;
            const far = clip && Math.abs(clip.start - minute) > 5;
            return (
              <button
                key={c.key}
                type="button"
                className={`jqc-tile${wide ? ' jqc-wide' : ''}`}
                disabled={!clip}
                onClick={() => clip && onOpenCamera(c.key, minute)}
                aria-label={clip ? `Play ${c.label} at ${fmtClock(minute)}` : `${c.label}: no recording`}
              >
                <span className={`jqc-frame ${wide ? 'jqc-r-wide' : 'jqc-r-std'}`}>
                  {clip && thumbs[c.key] && <img src={thumbs[c.key]} alt="" />}
                  {!clip && (
                    <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.faint, fontSize: 13 }}>No recording this day</span>
                  )}
                  {far && (
                    <span style={{ position: 'absolute', top: 8, left: 8, padding: '3px 9px', borderRadius: 999, background: 'rgba(20,14,4,.85)', color: C.warn, fontSize: 12, fontWeight: 600 }}>
                      Nearest clip {fmtClock(clip.start)}
                    </span>
                  )}
                </span>
                <span style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '9px 12px 11px', minWidth: 0 }}>
                  <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{c.station}</span>
                    {c.sub && <span style={{ color: C.muted, fontSize: 13 }}>{c.sub}</span>}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: C.textSoft, minWidth: 0 }}>
                    {c.jobqStation ? (
                      <>
                        <span className="jqc-dot" style={{ background: run ? (run.state === 'Working' ? KIND_DOT.working : KIND_DOT.maintenance) : KIND_DOT.idle }} />
                        <span className="jqc-ell">{run ? (run.job != null ? `Job ${run.job} · ${run.desc}${run.user ? ` · ${run.user}` : ''}` : 'Maintenance') : 'No JobQ work at this moment'}</span>
                      </>
                    ) : (
                      <span style={{ color: C.faint }}>Not a JobQ station</span>
                    )}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div className="jqc-card" style={{ padding: '14px 16px 10px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 12px', marginBottom: 12 }}>
          <h2 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{fmtDateLong(date)} across the shop</h2>
          <span style={{ color: C.faint, fontSize: 12 }}>JobQ work per station · click or drag to move every camera</span>
          <span style={{ flex: 1 }} />
          <div className="jqc-seg" role="group" aria-label="Time range">
            <button type="button" aria-pressed={activeRange === RANGE_WORKDAY} onClick={() => setRange('workday')}>6a – 10p</button>
            <button type="button" aria-pressed={activeRange === RANGE_FULL} onClick={() => setRange('full')}>24 h</button>
          </div>
        </div>
        <DayBar
          lanes={lanes}
          range={activeRange}
          playhead={minute}
          now={isToday ? centralMinute() : null}
          onSeek={onMinute}
          ariaLabel={`All cameras on ${fmtDateLong(date)}`}
        />
      </div>
    </main>
  );
}
